import * as React from "react";
import {
  Text,
  Button,
  Input,
  DialogActions,
  DialogContent,
  DialogTitle,
  Modal,
  Icon,
  Flex,
  EditableList,
  Menu,
  Divider,
  Radio,
  TooltipWrapper
} from "@theorgchartdev/storybook";
import { GetAccountMetadata } from "@/Utils/Promises";
import {
  OperatorList,
  FieldOptionsPlaceholder,
  SubtractSchema,
  BaseLookupSchema,
  BaseSplitSchema,
  ScopeForward,
  ScopeInverse,
  RulePropsForward,
  RulePropsInverse
} from "./Constants";
import { GenericForm } from "./GenericForm";
import { ConcatenateSettings } from "./ConcatenateSettings";
import { RollupSettings } from "./RollupSettings";
import { isEqual } from "lodash";
import CentralizedModalContainer from "@/appComponents/Blocks/Utils/CentralizedModalContainer/CentralizedModalContainer";
import cx from "classnames";
import { GetAllFieldNames, buildOrganizedFieldOptions } from "./Utils";
import { appstr } from "@coreapp/utils/appstr";
import { appstrp } from "@coreapp/utils/appstrp";
import { IsPositionChart } from "@coreapp/utils/IsPositionChart";
import { IsFormulaNameReserved } from "@coreapp/utils/IsFormulaNameReserved";
import { GetWSMgr } from "@/Utils/StateLib";

export const BuildFormulaOperations = (
  formulaElements,
  selectHandler,
  selectedIdx,
  operationErrors
) => {
  let options = [];
  if (!formulaElements) return options;
  for (let i = 0; i <= formulaElements.length - 1; i++) {
    let activeClass = i === selectedIdx ? `ocn-row-active` : null;
    let operation = formulaElements[i];
    let option = {};
    const hasError = !!operationErrors[i];
    option.value = i;
    option.clickHandler = selectHandler;
    option.content = (
      <Flex
        className={cx(activeClass)}
        alignItems="center"
        justifyContent="space-between"
        margin="0 8px 0 0"
      >
        <Text size={"small"}>{operation.type}</Text>
        {hasError && (
          <TooltipWrapper
            tooltipProps={{ message: appstr("There are required fields missing"), position: "top" }}
            className="formula-validation-error-tooltip"
          >
            <Icon size="small" name="circle-exclamation" modifier="error" />
          </TooltipWrapper>
        )}
      </Flex>
    );
    option.divider = true;
    options.push(option);
  }
  return options;
};

export const StaticFormulaEditorModal = ({
  chartConfig,
  name,
  onCreateFormula,
  onClose,
  creatingFormula,
  show,
  dataTestIdPrefix
}) => {
  const [formulaName, setFormulaName] = React.useState(creatingFormula ? "" : (name ?? ""));
  const [formulaElements, setFormulaElements] = React.useState([]);
  const [formulaClone, setFormulaClone] = React.useState(null);
  const [selectedElementIndex, setSelectedElementIndex] = React.useState(-1);
  const [operationSelectionDirtyCounter, setOperationSelectionDirtyCounter] = React.useState(0); // hacky
  const [availableReferenceTables, setAvailableReferenceTables] = React.useState([]);
  const [errors, setErrors] = React.useState({ name: "", operations: "" });
  const [operationErrors, setOperationErrors] = React.useState({});
  const [curIndex, setCurIndex] = React.useState(null);
  const [targetType, setTargetType] = React.useState("employee");

  React.useEffect(() => {
    GetAccountMetadata().then(({ result }) => {
      setAvailableReferenceTables(result.referenceTables?.map(rt => rt.tableName) ?? []);
    });
  }, []);

  const selectedElement = React.useMemo(() => {
    if (selectedElementIndex >= 0 && selectedElementIndex < formulaElements.length) {
      return formulaElements[selectedElementIndex];
    } else {
      return undefined;
    }
  }, [formulaElements, selectedElementIndex]);

  const selectedElementErrors = React.useMemo(() => {
    if (selectedElementIndex >= 0 && selectedElementIndex < formulaElements.length) {
      return operationErrors[selectedElementIndex];
    } else {
      return undefined;
    }
  }, [selectedElementIndex, formulaElements, operationErrors]);

  React.useEffect(() => {
    if (name) {
      // Get formulae from chartConfig
      // find the formula with the same name
      // If it exists, then use it to populate the state variables here
      // otherwise do nothing

      let formula = null;
      let formulas = chartConfig.formulas ?? [];
      for (const f in formulas) {
        let fm = formulas[f];
        if (fm.name === name) {
          setCurIndex(parseInt(f));
          formula = fm;
          break;
        }
      }

      if (formula !== null) {
        setFormulaName(formula.name);
        setFormulaClone(JSON.parse(JSON.stringify(formula)));
        setFormulaElements(formula.operations);
        if (formula.targetType) {
          setTargetType(formula.targetType);
        }
      }
    }
  }, []);

  const onChangeFormulaElements = newFormulaElements => {
    setFormulaElements(newFormulaElements);
    setOperationErrors({});
    setErrors(prev => ({ ...prev, operations: "" }));
  };

  const BuildOperationOptions = () => {
    let operationOptions = [];
    for (const operator of OperatorList) {
      operationOptions.push({
        label: operator,
        dataTestId: `${dataTestIdPrefix}-operation-${operator}`,
        value: operator,
        clickHandler: () => {
          const numFormulaElements = formulaElements.length;
          onChangeFormulaElements([
            ...formulaElements,
            {
              type: operator
            }
          ]);
          setSelectedElementIndex(numFormulaElements);
          setOperationSelectionDirtyCounter(operationSelectionDirtyCounter + 1);
        }
      });
    }
    return operationOptions;
  };

  let availableFields = [];
  if (chartConfig["headers"]) availableFields = chartConfig["headers"].sort();

  const organizedFieldOptions = React.useMemo(() => {
    if (!IsPositionChart(chartConfig)) {
      return availableFields.map(field => ({ label: field, value: field }));
    }

    const positionFields = chartConfig["positionFields"] || [];
    const holderFields = chartConfig["holderFields"] || [];

    return buildOrganizedFieldOptions(availableFields, positionFields, holderFields);
  }, [chartConfig, availableFields]);

  const schemaMap = createSchemaMap(availableFields, chartConfig);

  const onSortIndex = (oldIndex, newIndex) => {
    setSelectedElementIndex(newIndex);
  };

  const onRemoveOperation = removeIdx => {
    if (removeIdx >= 0 && removeIdx < formulaElements.length) {
      const updatedFormulaElements = [...formulaElements];
      updatedFormulaElements.splice(removeIdx, 1);
      onChangeFormulaElements(updatedFormulaElements);
      setSelectedElementIndex(-1);
      setOperationSelectionDirtyCounter(operationSelectionDirtyCounter + 1);
    }
  };

  const ValidateFormula = formulaDef => {
    const newErrors = { name: "", operations: "" };

    // Validate name
    if (formulaDef.name.length < 1) {
      newErrors.name = appstr("formulaerrnoname");
    } else {
      let invalidChar = [".", "$"];
      for (var c in invalidChar) {
        if (formulaDef.name.includes(invalidChar[c])) {
          newErrors.name = appstr("invalidcharwarn") + "(" + invalidChar + ")";
          break;
        }
      }

      if (!newErrors.name) {
        const existingFieldNames = GetAllFieldNames(chartConfig, (key, idx) => {
          if (creatingFormula || key !== "formulas") {
            return true;
          }
          return idx !== curIndex;
        });

        if (existingFieldNames.includes(formulaDef.name)) {
          newErrors.name = appstr("formulaerrdupname");
        }
      }

      if (!newErrors.name) {
        const curDoc = GetWSMgr()?.GetDoc();
        if (IsFormulaNameReserved(formulaDef.name, curDoc)) {
          newErrors.name = `'${formulaDef.name}' ${appstr("formulareservedname")} ${appstr("formulareservedname2")}`;
        }
      }
    }

    // Validate operations
    const operationValidation = validateOperations(formulaDef.operations);
    if (operationValidation.formula) {
      newErrors.operations = operationValidation.formula;
    } else if (Object.keys(operationValidation).length > 0) {
      setOperationErrors(operationValidation);
    }

    setErrors(newErrors);
    return (
      !newErrors.name && !newErrors.operations && Object.keys(operationValidation).length === 0
    );
  };

  const SaveChanges = () => {
    const updatedFormula = {
      name: formulaName.trim(),
      operations: formulaElements,
      targetType: targetType
    };
    const isPosition = IsPositionChart(chartConfig);

    let isValid = ValidateFormula(updatedFormula);
    if (!isValid) return false;

    let formulas = [];
    if (chartConfig.formulas) {
      formulas = JSON.parse(JSON.stringify(chartConfig.formulas));
    }

    // find the formula
    // Use the name of the formula that this component was initialized with.
    //const formulaIndex = formulas.map(item => item?.name).indexOf(formulaName);
    let headers = JSON.parse(JSON.stringify(chartConfig.headers));
    let updateKeys = ["headers", "formulas"];
    let holderFields = [...(chartConfig.holderFields || [])];
    let positionFields = [...(chartConfig.positionFields || [])];

    if (isPosition) {
      updateKeys.push("positionFields");
      updateKeys.push("holderFields");
    }

    if (curIndex != null && curIndex > -1) {
      formulas[curIndex] = updatedFormula;
      if (formulaClone && formulaClone.name !== formulaName) {
        let oldIdx = headers.indexOf(formulaClone.name);
        if (oldIdx != null && oldIdx > -1) {
          headers[oldIdx] = formulaName;
        }

        if (isPosition) {
          if (formulaClone.targetType === "employee") {
            let oldHolderIdx = holderFields.indexOf(formulaClone.name);
            if (oldHolderIdx != null && oldHolderIdx > -1) {
              holderFields.splice(oldHolderIdx, 1);
            }
          } else if (formulaClone.targetType === "position") {
            let oldPositionIdx = positionFields.indexOf(formulaClone.name);
            if (oldPositionIdx != null && oldPositionIdx > -1) {
              positionFields.splice(oldPositionIdx, 1);
            }
          }

          if (targetType === "employee" && !holderFields.includes(formulaName)) {
            holderFields.push(formulaName);
          } else if (targetType === "position" && !positionFields.includes(formulaName)) {
            positionFields.push(formulaName);
          }
        }
      }
    } else {
      headers.push(formulaName);
      formulas.push(updatedFormula);

      if (isPosition) {
        if (targetType === "employee" && !holderFields.includes(formulaName)) {
          holderFields.push(formulaName);
        } else if (targetType === "position" && !positionFields.includes(formulaName)) {
          positionFields.push(formulaName);
        }
      }
    }

    let updateBlock = isPosition
      ? { formulas: formulas, headers: headers, holderFields, positionFields }
      : { formulas: formulas, headers: headers };

    onCreateFormula(true, updateBlock, updateKeys);
  };

  const onSelectOperation = o => {
    setSelectedElementIndex(o.value);
  };

  const handleOperationUpdate = updater => {
    const nextOperations = updater(
      BuildFormulaOperations(
        formulaElements,
        onSelectOperation,
        selectedElementIndex,
        operationErrors
      )
    );

    const updatedFormulaElements = [...formulaElements];

    if (nextOperations?.length !== updatedFormulaElements?.length) return;

    const newFormulaElements = nextOperations.map(({ value }) => updatedFormulaElements[value]);
    onChangeFormulaElements(newFormulaElements);
  };

  const createTrigger = (
    <Button
      dataTestId={"add-operation-button"}
      size={"medium"}
      modifier={"secondary"}
      iconOnly={true}
      iconProps={{ name: "plus" }}
    />
  );

  return (
    <CentralizedModalContainer show={show}>
      <Modal
        id="create-formula-modal"
        show={show}
        centerScreen={true}
        customStyle={{ width: "1000px", height: "700px", minWidth: "900px", minHeight: "600px" }}
        className={"oc-modal-content"}
      >
        <DialogTitle
          divider={true}
          onClose={() => onClose()}
          data-testid={`${dataTestIdPrefix}-static-formula-modal-title`}
        >
          <span className={"ocn-explorer-icon-label"}>
            <Icon size={"medium"} name={"function"} />
            {creatingFormula ? appstr("Create formula") : appstr("Edit formula")}
          </span>
        </DialogTitle>
        <DialogContent customContentClass={"oc-modal-content-container"}>
          <Flex
            className={"oc-modal-content oc-formula-editor-content"}
            direction={"column"}
            gap={"24px"}
            height={"100%"}
          >
            <Flex direction={"column"} gap={"24px"}>
              <Input
                data-testid={"static-formula-modal-input-name"}
                type="text"
                size="medium"
                value={formulaName}
                onChange={e => {
                  setErrors(prev => ({ ...prev, name: "" }));
                  setFormulaName(e.target.value);
                }}
                label={appstr("Formula name")}
                message={errors.name}
                placeholder={appstr("Enter formula name")}
                status={errors.name ? "error" : null}
                require={true}
                className={"oc-formula-editor-input"}
              />
              {IsPositionChart(chartConfig) && (
                <Flex direction={"column"} gap={"8px"}>
                  <Text size={"small"} modifier={"bold"}>
                    {appstrp("Apply this formula to", ":")}
                  </Text>
                  <Flex gap={"16px"}>
                    <Radio
                      id="employee-target-type"
                      name="target-type"
                      label={appstr("Employee")}
                      checked={targetType === "employee"}
                      onChange={() => setTargetType("employee")}
                      size="medium"
                      data-testid={"formula-target-type-employee"}
                    />
                    <Radio
                      id="position-target-type"
                      name="target-type"
                      label={appstr("Position")}
                      checked={targetType === "position"}
                      onChange={() => setTargetType("position")}
                      size="medium"
                      data-testid={"formula-target-type-position"}
                    />
                  </Flex>
                </Flex>
              )}
            </Flex>
            <Flex width={"100%"} flex={"1"} overflow={"hidden"}>
              <Flex width={"100%"} height={"100%"}>
                <Flex gap={"8px"} width={"100%"} height={"100%"}>
                  <Flex
                    direction={"column"}
                    gap={"4px"}
                    overflow={"hidden"}
                    width={"60%"}
                    height={"100%"}
                  >
                    <Flex direction={"column"} gap={"4px"} width={"100%"} height={"100%"}>
                      <Flex
                        justifyContent={"space-between"}
                        gap={"4px"}
                        alignItems={"center"}
                        width={"100%"}
                      >
                        <Text modifier={"bold"} size={"medium"}>
                          {appstr("Operations")}
                          <span className={"ocn-form-label-required"}> *</span>
                        </Text>
                        <Flex className={"oc-right-justify"}>
                          <Menu
                            dataTestId={`${dataTestIdPrefix}-add-operation-button`}
                            trigger={createTrigger}
                            options={BuildOperationOptions()}
                            listWidth={"medium"}
                            listHeader={appstr("Choose operation")}
                            value={selectedElementIndex}
                            showCheck={true}
                          />
                        </Flex>
                      </Flex>
                      <Flex width={"100%"} direction={"column"} overflow={"auto"} flex={"1"}>
                        <div className={cx("oc-list-container", "ocn-list-scrollbar")}>
                          {formulaElements && formulaElements.length > 0 ? (
                            <EditableList
                              disableSort={false}
                              disableRemove={false}
                              showCheck={false}
                              listWidth="fill"
                              options={BuildFormulaOperations(
                                formulaElements,
                                onSelectOperation,
                                selectedElementIndex,
                                operationErrors
                              )}
                              setOptions={handleOperationUpdate}
                              onRemoveValues={onRemoveOperation}
                              onSortIndex={onSortIndex}
                              value={selectedElementIndex}
                            />
                          ) : (
                            <Flex direction={"column"}>
                              <Text size={"small"} modifier={"lightgray"}>
                                {appstr("Click + to add an operation")}
                              </Text>
                              {errors.operations && (
                                <Text colorModifier={"error"} size={"xsmall"}>
                                  {errors.operations}
                                </Text>
                              )}
                            </Flex>
                          )}
                        </div>
                      </Flex>
                    </Flex>
                  </Flex>
                  <Flex>
                    <Divider vertical={true} />
                  </Flex>
                  <Flex direction={"column"} gap={"4px"} height={"100%"} width={"100%"}>
                    <Text modifier={"bold"} size={"medium"}>
                      {appstr("Settings")}
                    </Text>
                    <div className={cx("oc-list-container", "ocn-list-scrollbar")}>
                      {selectedElementIndex != null && selectedElementIndex > -1 ? (
                        <FormulaSettingsComposite
                          key={`settings-menu-${operationSelectionDirtyCounter}`}
                          schemaMap={schemaMap}
                          selectedElement={selectedElement}
                          selectedElementIndex={selectedElementIndex}
                          onUpdate={updatedFormulaElement => {
                            const updatedFormulaElements = [...formulaElements];
                            updatedFormulaElements[selectedElementIndex] = updatedFormulaElement;
                            if (!isEqual(formulaElements, updatedFormulaElements)) {
                              onChangeFormulaElements(updatedFormulaElements);
                            }
                          }}
                          fields={availableFields}
                          organizedFieldOptions={organizedFieldOptions}
                          referenceTables={availableReferenceTables}
                          chartConfig={chartConfig}
                          errors={selectedElementErrors}
                          dataTestIdPrefix={dataTestIdPrefix}
                        />
                      ) : (
                        <Text size={"small"} modifier={"lightgray"}>
                          {formulaElements && formulaElements.length > 0
                            ? appstr("Select an operation to configure")
                            : null}
                        </Text>
                      )}
                    </div>
                  </Flex>
                </Flex>
              </Flex>
            </Flex>
          </Flex>
        </DialogContent>
        <DialogActions divider={true}>
          <DialogActions.Tertiary
            dataTestId={"static-formula-modal-action-cancel"}
            onClick={() => onClose()}
          >
            {appstr("Cancel")}
          </DialogActions.Tertiary>
          <DialogActions.Primary
            dataTestId={"static-formula-modal-action-save"}
            onClick={SaveChanges}
          >
            {appstr("Save")}
          </DialogActions.Primary>
        </DialogActions>
      </Modal>
    </CentralizedModalContainer>
  );
};

const createSchemaMap = (fields, chartConfig) => {
  const setFieldOptions = baseSchema => {
    return baseSchema.map(input => {
      if (input.options && input.options === FieldOptionsPlaceholder) {
        const copy = { ...input };
        copy.options = fields;
        copy.isFieldSelector = true;
        return copy;
      } else {
        return input;
      }
    });
  };

  const createLookupSchema = () => {
    let lookupSchema = [...BaseLookupSchema];

    return setFieldOptions(lookupSchema);
  };

  return {
    add: [], // no settings
    subtract: SubtractSchema,
    multiply: [], // no settings
    divide: [], // no settings
    lookup: createLookupSchema(),
    split: setFieldOptions(BaseSplitSchema)
  };
};

const mapRuleForward = (rule, isPositionChart) => {
  let ruleField = undefined;
  let ruleValue = undefined;

  for (const key in rule.rule) {
    ruleField = key;
    ruleValue = rule.rule[key];
  }

  const isFieldToFieldComparison =
    typeof ruleValue === "object" && ruleValue && (ruleValue.fieldName || ruleValue.fieldId);

  const propsToFieldComparison = {
    $eq: "equals",
    $ne: "notequal",
    $gt: "greater",
    $lt: "less",
    $gte: "greaterequal",
    $lte: "lessequal"
  };

  const ruleData = {
    ruleType: rule.props
      ? isFieldToFieldComparison && propsToFieldComparison[rule.props]
        ? propsToFieldComparison[rule.props]
        : RulePropsForward[rule.props]
      : "Equal",
    field: ruleField,
    isFieldToField: !!isFieldToFieldComparison,
    comparisonValue: ruleValue,
    comparisonField: isFieldToFieldComparison
      ? ruleValue.fieldName || ruleValue.fieldId
      : undefined,
    comparisonFieldClassType: isFieldToFieldComparison ? ruleValue.fieldClassName : undefined,
    staticRange: rule.staticRange === true,
    staticRangeFrom: rule.range?.from ?? undefined,
    staticRangeTo: rule.range?.to ?? undefined,
    durationUnit: rule.range ? rule.range.period : undefined,
    bounds: rule.bounds
  };

  if (isPositionChart && rule.fieldClassType) {
    ruleData.fieldClassType = rule.fieldClassType;
  }

  return ruleData;
};

export const mapSettingsForward = (s, chartConfig = null) => {
  const { type, rules, scope, field, self } = s;
  const isPositionChart = chartConfig && IsPositionChart(chartConfig);

  switch (type) {
    case "add":
    case "multiply":
    case "divide":
    case "subtract":
      return s;

    case "lookup": {
      const lookupData = {
        type,
        lookupUsing: s.field?.tgtfield,
        matchUsing: s.field?.myfield,
        returnUsing: s.field?.rtnfield
      };

      if (isPositionChart && s.field) {
        if (s.field.tgtfieldClassType) lookupData.lookupUsingClassType = s.field.tgtfieldClassType;
        if (s.field.myfieldClassType) lookupData.matchUsingClassType = s.field.myfieldClassType;
        if (s.field.rtnfieldClassType) lookupData.returnUsingClassType = s.field.rtnfieldClassType;
      }

      return lookupData;
    }

    case "split": {
      const splitData = {
        type,
        fieldLookup: s.values?.[0]?.value ?? "",
        separator: s.character,
        index: s.index
      };

      if (isPositionChart && s.values?.[0]?.fieldClassType) {
        splitData.fieldLookupClassType = s.values[0].fieldClassType;
      }

      return splitData;
    }

    case "concatenate": {
      return s;
    }

    case "average":
    case "count":
    case "sum":
    case "min":
    case "max": {
      const rollupData = {
        type,
        rules: rules?.map(r => mapRuleForward(r, isPositionChart)) ?? [],
        scope: ScopeForward[scope],
        field,
        self
      };

      if (isPositionChart && s.fieldClassType) {
        rollupData.fieldClassType = s.fieldClassType;
      }

      return rollupData;
    }

    default:
      console.warn(`Unknown operation type in mapSettingsForward: ${type}`);
      return s;
  }
};

const mapRuleInverse = r => {
  let ruleValue;

  if (r.isFieldToField) {
    ruleValue = {
      fieldName: r.comparisonField,
      fieldId: r.comparisonField,
      fieldClassName: r.comparisonFieldClassType || null
    };
  } else {
    ruleValue = r.comparisonValue;
  }

  const ruleObj = {};

  const ruleTypeToProps = {
    In: "$in",
    "Not In": "$nin",
    "Greater Than": "$gt",
    "Less Than": "$lt",
    "Not Equal": "$ne",
    Equal: "$eq",
    "Date Bracket": "Date Bracket",
    equal: "$eq",
    notequal: "$ne",
    greater: "$gt",
    less: "$lt",
    greaterequal: "$gte",
    lessequal: "$lte"
  };

  // Normalize "equals" to "equal" for consistent mapping
  const normalizedRuleType = r.ruleType === "equals" ? "equal" : r.ruleType;

  const currentRuleType =
    ruleTypeToProps[normalizedRuleType] || RulePropsInverse[normalizedRuleType];
  const validRuleTypes = Object.keys(RulePropsForward);

  if (r.isFieldToField) {
    // Field-to-field comparisons always get props
    const propsValue = ruleTypeToProps[normalizedRuleType];
    if (propsValue) {
      ruleObj.props = propsValue;
    }
  } else {
    // Standard comparisons only get props if not "Equal"
    if (r.ruleType !== "Equal") {
      const propsValue = ruleTypeToProps[normalizedRuleType];
      if (propsValue) {
        ruleObj.props = propsValue;
      }
    }
  }

  if (
    ruleObj.props !== "$in" &&
    ruleObj.props !== "$nin" &&
    Array.isArray(ruleValue) &&
    !r.isFieldToField
  ) {
    ruleValue = ruleValue[0];
  }

  if (r.field && ruleValue != null) {
    ruleObj.rule = { [r.field]: ruleValue };
  }

  if (r.ruleType === "Date Bracket") {
    ruleObj.staticRange = r.staticRange;
    ruleObj.range =
      r.staticRangeFrom || r.staticRangeTo || r.durationUnit
        ? {
            from: r.staticRangeFrom,
            to: r.staticRangeTo,
            period: r.durationUnit
          }
        : undefined;
    ruleObj.bounds = r.bounds;
  }

  if (r.fieldClassType) {
    ruleObj.fieldClassType = r.fieldClassType;
  }

  return ruleObj;
};

export const mapSettingsInverse = data => {
  const { type } = data;

  switch (type) {
    case "add":
    case "multiply":
    case "divide":
    case "subtract":
      return data;

    case "lookup": {
      if (!data.lookupUsing && !data.matchUsing && !data.returnUsing) {
        return { type, field: undefined };
      }

      const fieldData = {
        tgtfield: data.lookupUsing,
        myfield: data.matchUsing,
        rtnfield: data.returnUsing
      };

      if (data.lookupUsingClassType) fieldData.tgtfieldClassType = data.lookupUsingClassType;
      if (data.matchUsingClassType) fieldData.myfieldClassType = data.matchUsingClassType;
      if (data.returnUsingClassType) fieldData.rtnfieldClassType = data.returnUsingClassType;

      return {
        type,
        field: fieldData
      };
    }

    case "split": {
      const splitValue = {
        type: "lookup",
        value: data.fieldLookup
      };

      if (data.fieldLookupClassType) {
        splitValue.fieldClassType = data.fieldLookupClassType;
      }

      return {
        type,
        values: [splitValue],
        character: data.separator,
        index: parseInt(data.index, 10)
      };
    }

    case "concatenate": {
      return data;
    }

    case "average":
    case "count":
    case "sum":
    case "min":
    case "max": {
      const operationObj = {
        type,
        rules: data.rules?.map(r => mapRuleInverse(r)) ?? [],
        scope: ScopeInverse[data.scope],
        field: data.field,
        self: data.self
      };

      if (data.fieldClassType) {
        operationObj.fieldClassType = data.fieldClassType;
      }

      if (type === "count") {
        delete operationObj.field;
      }

      return operationObj;
    }

    default:
      console.warn(`Unknown operation type in mapSettingsInverse: ${type}`);
      return data;
  }
};

export const validateOperations = operations => {
  if (!operations || operations.length < 1) {
    return { formula: appstr("formulaerrnooperations") };
  }

  return operations.reduce((acc, operation, index) => {
    const { type, rules, field, values } = operation;

    switch (type) {
      case "lookup":
        const lookupErrs = {};
        if (!field?.tgtfield) lookupErrs["lookupUsing"] = appstr("This field is required");
        if (!field?.myfield) lookupErrs["matchUsing"] = appstr("This field is required");
        if (!field?.rtnfield) lookupErrs["returnUsing"] = appstr("This field is required");
        if (Object.keys(lookupErrs).length > 0) acc[index] = lookupErrs;
        return acc;
      case "split":
        if (!values?.[0]?.value) {
          acc[index] = { fieldLookup: appstr("This field is required") };
        }
        return acc;
      case "concatenate":
        if (!values || values.length === 0) {
          acc[index] = { value: appstr("Add at least one value to the operation") };
        }
        return acc;
      case "average":
      case "count":
      case "sum":
      case "min":
      case "max":
        const ruleErrs = {};
        rules?.forEach((rule, i) => {
          if (!rule.rule || Object.keys(rule.rule).length === 0) {
            ruleErrs[i] = { field: appstr("This field is required") };
          }
        });
        if (Object.keys(ruleErrs).length > 0) acc[index] = ruleErrs;
        return acc;
      default:
        return acc;
    }
  }, {});
};

const FormulaSettingsComposite = ({
  selectedElement,
  selectedElementIndex,
  onUpdate,
  schemaMap,
  fields,
  organizedFieldOptions,
  referenceTables,
  chartConfig,
  errors,
  dataTestIdPrefix
}) => {
  if (selectedElement) {
    const operator = selectedElement;

    return (
      <Flex
        className={"create-formula-settings-menu"}
        direction={"column"}
        padding={"12px"}
        width={"100%"}
        height={"100%"}
        key={`fsc-${selectedElementIndex}`}
      >
        {(() => {
          if (["average", "count", "sum", "min", "max"].includes(operator.type)) {
            return (
              <RollupSettings
                operator={operator.type}
                fields={fields}
                organizedFieldOptions={organizedFieldOptions}
                chartConfig={chartConfig}
                onUpdate={formData => {
                  onUpdate(mapSettingsInverse(formData));
                }}
                initialValues={mapSettingsForward(operator, chartConfig)}
                dataTestIdPrefix={operator.type}
                errors={errors}
              />
            );
          } else if (operator.type === "concatenate") {
            return (
              <ConcatenateSettings
                onUpdate={formData => {
                  onUpdate(mapSettingsInverse(formData));
                }}
                initialValues={mapSettingsForward(operator, chartConfig)}
                fields={fields}
                organizedFieldOptions={organizedFieldOptions}
                referenceTables={referenceTables}
                chartConfig={chartConfig}
                errors={errors}
                dataTestIdPrefix={`${dataTestIdPrefix}-${operator.type}`}
              />
            );
          } else {
            return (
              <GenericForm
                formSchema={schemaMap[operator.type]}
                onUpdate={formData => {
                  onUpdate(mapSettingsInverse(formData));
                }}
                initialValues={mapSettingsForward(operator, chartConfig)}
                organizedFieldOptions={organizedFieldOptions}
                chartConfig={chartConfig}
                errors={errors}
                dataTestIdPrefix={`${dataTestIdPrefix}-${operator.type}`}
              />
            );
          }
        })()}
      </Flex>
    );
  } else {
    return <div></div>;
  }
};
